/**
 * MessagingPage — Unified messaging hub
 *
 * Layout: 3-panel
 *   Left panel  — Email Accounts (with auto/manual toggle)
 *   Center panel — Email conversations list (with status labels + filtering)
 *   Right panel  — Conversation detail + attachment preview (slide dock)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, Search, Loader2, ChevronRight, Paperclip, X, FileText,
  Bot, User, AlertCircle, CheckCircle, Clock, Play, RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DESKTOP_STANDARD_SHEET_WIDTH_CLASS } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────

interface EmailAccount {
  id: string;
  email_address: string;
  display_name?: string | null;
  purpose?: string;
  is_active?: boolean;
  process_mode?: string; // 'auto' | 'manual'
}

interface ConversationRow {
  id: string;
  channel?: string | null;
  status?: string | null;
  subject?: string | null;
  from_identifier?: string | null;
  fromIdentifier?: string | null;
  last_message_at?: string | null;
  lastMessageAt?: string | null;
  process_status?: string; // 'pending' | 'processed' | 'error'
  processStatus?: string;
  email_account_id?: string | null;
  has_attachments?: boolean;
}

interface MessageRow {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_id?: string | null;
  content: string;
  channel?: string | null;
  direction?: string | null;
  has_attachments?: boolean;
  data?: Record<string, unknown>;
  channel_data?: Record<string, unknown>;
  created_at: string;
}

interface AttachmentRow {
  id: string;
  file_name: string;
  file_url: string;
  mime_type?: string;
  file_size?: number;
  doc_type?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

// ─── Status label helpers ────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pending: {
    label: 'Pending',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Clock,
  },
  processed: {
    label: 'Processed',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: CheckCircle,
  },
  error: {
    label: 'Error',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
  },
};

function ProcessStatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG['pending']!;
  const Icon = config!.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium', config!.color)}>
      <Icon className="h-3 w-3" />
      {config!.label}
    </span>
  );
}

// ─── Component ───────────────────────────────────────

export default function MessagingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // ─ Email Accounts
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    searchParams.get('accountId'),
  );

  // ─ Conversations
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [convsLoading, setConvsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // ─ Detail panel
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // ─ Attachment preview
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentRow | null>(null);
  const [attachmentsCache, setAttachmentsCache] = useState<Record<string, AttachmentRow[]>>({});

  // ─── Debounced search ──────────────────────────────
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  // ─── Load email accounts ──────────────────────────
  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await api.get<PaginatedResponse<EmailAccount>>('/email-accounts?limit=100');
      setAccounts(res.items ?? []);
    } catch {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ─── Load conversations ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    setConvsLoading(true);
    const params = new URLSearchParams({ page: '1', limit: '100', channel: 'email' });
    if (search) params.set('search', search);
    if (statusFilter !== 'all') params.set('processStatus', statusFilter);
    if (selectedAccountId) params.set('emailAccountId', selectedAccountId);

    api.get<PaginatedResponse<ConversationRow>>(`/conversations?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setConversations(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setConvsLoading(false);
      });

    return () => { cancelled = true; };
  }, [search, statusFilter, selectedAccountId]);

  // ─── Load messages when conversation selected ─────
  useEffect(() => {
    if (!selectedConvId) { setMessages([]); return; }
    let cancelled = false;
    setMessagesLoading(true);

    api.get<PaginatedResponse<MessageRow>>(`/conversations/${selectedConvId}/messages?limit=100`)
      .then((res) => {
        if (!cancelled) setMessages(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setMessagesLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedConvId]);

  // ─── Toggle process mode on account ───────────────
  const toggleProcessMode = useCallback(async (accountId: string, currentMode: string) => {
    const newMode = currentMode === 'auto' ? 'manual' : 'auto';
    try {
      await api.patch(`/email-accounts/${accountId}`, { processMode: newMode });
      setAccounts((prev) =>
        prev.map((a) => (a.id === accountId ? { ...a, process_mode: newMode } : a)),
      );
    } catch {
      // silent — will reload
    }
  }, []);

  // ─── Click email account → filter conversations ───
  const handleAccountClick = useCallback((accountId: string) => {
    const newId = selectedAccountId === accountId ? null : accountId;
    setSelectedAccountId(newId);
    setSelectedConvId(null);
    if (newId) {
      setSearchParams({ accountId: newId });
    } else {
      setSearchParams({});
    }
  }, [selectedAccountId, setSearchParams]);

  // ─── Manual process trigger ───────────────────────
  const handleProcess = useCallback(async (convId: string) => {
    try {
      await api.post(`/conversations/${convId}/process`, {});
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, process_status: 'processed', processStatus: 'processed' } : c)),
      );
    } catch {
      // silent
    }
  }, []);

  // ─── Load attachments for a message ───────────────
  const loadAttachments = useCallback(async (messageId: string) => {
    if (attachmentsCache[messageId]) return attachmentsCache[messageId];
    try {
      const items = await api.get<AttachmentRow[]>(`/messages/${messageId}/attachments`);
      setAttachmentsCache((prev) => ({ ...prev, [messageId]: items }));
      return items;
    } catch {
      return [];
    }
  }, [attachmentsCache]);

  // ─── Selected conversation detail ─────────────────
  const selectedConv = useMemo(
    () => conversations.find((c) => c.id === selectedConvId),
    [conversations, selectedConvId],
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId),
    [accounts, selectedAccountId],
  );

  return (
    <div className="flex h-full overflow-hidden">
      {/* ═══ LEFT PANEL — Email Accounts ═══ */}
      <div className="flex w-[260px] shrink-0 flex-col border-r bg-muted/20">
        <div className="flex h-12 items-center gap-2 border-b px-4">
          <Mail className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">Email Accounts</h2>
          <button onClick={loadAccounts} className="ml-auto text-muted-foreground hover:text-foreground" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {accountsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : accounts.length === 0 ? (
              <p className="px-2 py-4 text-xs text-muted-foreground text-center">No email accounts configured</p>
            ) : (
              accounts.map((acct) => {
                const isAuto = acct.process_mode !== 'manual';
                const isSelected = acct.id === selectedAccountId;
                return (
                  <div
                    key={acct.id}
                    className={cn(
                      'rounded-lg border p-3 transition-colors cursor-pointer',
                      isSelected
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-transparent hover:bg-muted/60',
                    )}
                    onClick={() => handleAccountClick(acct.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm font-medium">
                        {acct.display_name || acct.email_address}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground pl-5">
                      {acct.email_address}
                    </p>

                    {/* Auto/Manual toggle */}
                    <div
                      className="mt-2 flex items-center gap-2 pl-5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        checked={isAuto}
                        onCheckedChange={() => toggleProcessMode(acct.id, acct.process_mode ?? 'auto')}
                        className="h-4 w-7 data-[state=checked]:bg-emerald-500"
                      />
                      <span className="text-[11px] text-muted-foreground">
                        {isAuto ? (
                          <span className="flex items-center gap-1">
                            <Bot className="h-3 w-3" /> Auto
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" /> Manual
                          </span>
                        )}
                      </span>
                      {acct.purpose && acct.purpose !== 'general' && (
                        <Badge variant="outline" className="ml-auto text-[9px] uppercase">
                          {acct.purpose}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* ═══ CENTER PANEL — Conversations ═══ */}
      <div className="flex min-w-0 flex-1 flex-col border-r">
        {/* Header */}
        <div className="flex h-12 items-center gap-2 border-b px-4">
          <h2 className="text-sm font-semibold">
            Email Messages
            {selectedAccount && (
              <span className="ml-2 font-normal text-muted-foreground">
                — {selectedAccount.display_name || selectedAccount.email_address}
              </span>
            )}
          </h2>
          <span className="ml-auto text-xs text-muted-foreground">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Search + filter bar */}
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search subject / sender…"
              className="h-8 pl-8 text-sm"
            />
          </div>
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="shrink-0">
            <TabsList className="h-8">
              <TabsTrigger value="all" className="text-xs px-3 h-7">All</TabsTrigger>
              <TabsTrigger value="pending" className="text-xs px-3 h-7">
                <Clock className="mr-1 h-3 w-3" /> Pending
              </TabsTrigger>
              <TabsTrigger value="processed" className="text-xs px-3 h-7">
                <CheckCircle className="mr-1 h-3 w-3" /> Processed
              </TabsTrigger>
              <TabsTrigger value="error" className="text-xs px-3 h-7">
                <AlertCircle className="mr-1 h-3 w-3" /> Error
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Conversation list */}
        <ScrollArea className="flex-1">
          {convsLoading ? (
            <div className="flex min-h-[180px] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : conversations.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No email conversations found.
            </div>
          ) : (
            <div className="divide-y">
              {conversations.map((conv) => {
                const sender = conv.fromIdentifier ?? conv.from_identifier ?? 'Unknown';
                const lastAt = conv.lastMessageAt ?? conv.last_message_at;
                const pStatus = conv.processStatus ?? conv.process_status ?? 'pending';
                const isSelected = conv.id === selectedConvId;
                return (
                  <button
                    key={conv.id}
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/40',
                    )}
                    onClick={() => setSelectedConvId(conv.id)}
                  >
                    <div className="mt-0.5 shrink-0">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium">{conv.subject || 'No subject'}</p>
                        <ProcessStatusBadge status={pStatus} />
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{sender}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {lastAt ? new Date(lastAt).toLocaleString() : 'No messages'}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {pStatus === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px]"
                          onClick={(e) => { e.stopPropagation(); handleProcess(conv.id); }}
                        >
                          <Play className="mr-1 h-3 w-3" /> Process
                        </Button>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* ═══ RIGHT PANEL — Conversation Detail + Attachment Preview ═══ */}
      <div className="flex w-[420px] shrink-0 flex-col bg-background">
        {selectedConvId ? (
          <>
            {/* Detail header */}
            <div className="flex h-12 items-center gap-2 border-b px-4">
              <h3 className="truncate text-sm font-semibold">
                {selectedConv?.subject || 'Conversation'}
              </h3>
              <button
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => { setSelectedConvId(null); setPreviewAttachment(null); }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1">
              {messagesLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No messages in this conversation.
                </p>
              ) : (
                <div className="space-y-3 p-4">
                  {messages.map((msg) => {
                    const isInbound = msg.direction === 'inbound';
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          'rounded-lg border p-3',
                          isInbound ? 'bg-blue-50/50 border-blue-100' : 'bg-muted/30',
                        )}
                      >
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {isInbound ? (
                            <User className="h-3 w-3" />
                          ) : (
                            <Bot className="h-3 w-3" />
                          )}
                          <span className="font-medium">
                            {msg.sender_type === 'agent' ? 'AI Agent' : (msg.sender_id || 'Unknown')}
                          </span>
                          <span className="ml-auto">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">
                          {msg.content?.slice(0, 2000)}
                          {(msg.content?.length ?? 0) > 2000 && '…'}
                        </div>

                        {/* Attachments */}
                        {msg.has_attachments && (
                          <MessageAttachments
                            messageId={msg.id}
                            loadAttachments={loadAttachments}
                            onPreview={setPreviewAttachment}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Mail className="mx-auto h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">Select a conversation to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* ═══ ATTACHMENT PREVIEW DOCK (right overlay) ═══ */}
      {previewAttachment && (
        <div className={cn('fixed inset-y-0 right-0 z-50 flex flex-col border-l bg-background shadow-2xl animate-in slide-in-from-right', DESKTOP_STANDARD_SHEET_WIDTH_CLASS)}>
          <div className="flex h-12 items-center gap-2 border-b px-4">
            <FileText className="h-4 w-4 text-primary" />
            <h3 className="truncate text-sm font-semibold">{previewAttachment.file_name}</h3>
            <span className="ml-auto text-xs text-muted-foreground">
              {previewAttachment.mime_type}
              {previewAttachment.file_size ? ` · ${formatFileSize(previewAttachment.file_size)}` : ''}
            </span>
            <button
              className="ml-2 text-muted-foreground hover:text-foreground"
              onClick={() => setPreviewAttachment(null)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            {isPdfMime(previewAttachment.mime_type) ? (
              <iframe
                src={previewAttachment.file_url}
                className="h-full w-full border-0"
                title={previewAttachment.file_name}
              />
            ) : isImageMime(previewAttachment.mime_type) ? (
              <div className="flex h-full items-center justify-center p-4">
                <img
                  src={previewAttachment.file_url}
                  alt={previewAttachment.file_name}
                  className="max-h-full max-w-full object-contain rounded"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <FileText className="h-12 w-12 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">Preview not available for this file type</p>
                <a
                  href={previewAttachment.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Download file
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MessageAttachments sub-component ────────────────

function MessageAttachments({
  messageId,
  loadAttachments,
  onPreview,
}: {
  messageId: string;
  loadAttachments: (id: string) => Promise<AttachmentRow[]>;
  onPreview: (att: AttachmentRow) => void;
}) {
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleLoad = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    const items = await loadAttachments(messageId);
    setAttachments(items);
    setLoaded(true);
    setLoading(false);
  }, [messageId, loadAttachments, loaded]);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading attachments…
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <Paperclip className="h-3 w-3" /> Attachments available
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1">
      {attachments.map((att) => (
        <button
          key={att.id}
          className="flex w-full items-center gap-2 rounded border bg-background px-2 py-1.5 text-left text-xs hover:bg-muted/40 transition-colors"
          onClick={() => onPreview(att)}
        >
          <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{att.file_name}</span>
          {att.doc_type && (
            <Badge variant="outline" className="ml-auto text-[9px] shrink-0">{att.doc_type}</Badge>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────

function isPdfMime(mime?: string): boolean {
  return mime === 'application/pdf';
}

function isImageMime(mime?: string): boolean {
  return !!mime && mime.startsWith('image/');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
